<template>
  <div class="w-full top-0 background-1 sticky z-20">
    <div
      class="px-6 md:px-12 pt-10 pb-4 hidden lg:flex justify-between items-center"
    >
      <div class="font-bold capitalize text-root-2 text-2xl">
        <div v-if="routeName === 'dashboard' || routeName === 'terminal'">
          <h3 class="text-lg md:text-2xl">
            {{ getTimeofDay }}, {{ user.firstName }}
          </h3>
          <div
            v-if="routeName === 'dashboard'"
            class="font-normal text-gray-10 text-xs md:text-sm"
          >
            Overview Summary
          </div>
        </div>
        <div class="capitalize" v-else>
          {{ pageTitle }}
        </div>
      </div>
      <div class="hidden lg:flex items-center">
        <div
          v-if="depositWithdrawAccess"
          class="flex text-white justify-center"
        >
          <el-tooltip
            class="item"
            effect="light"
            :disabled="
              isbusinessAccountActive || depositAccess.includes(user.kind)
            "
            :content="
              isbusinessAccountActive
                ? 'Business account currently not active'
                : 'No deposit access'
            "
            placement="bottom"
          >
            <div
              v-loading="isLoading"
              @click="goToDeposit"
              :class="
                isLoading ||
                !isbusinessAccountActive ||
                !depositAccess.includes(user.kind)
                  ? 'cursor-not-allowed opacity-60'
                  : 'cursor-pointer'
              "
              class="bg-primary font-normal cursor-pointer hover:opacity-80 flex items-center rounded-3xl py-2 px-6 mr-2"
            >
              + Deposit
            </div>
          </el-tooltip>
          <el-tooltip
            class="item"
            effect="light"
            :disabled="withdrawalAccess.includes(user.kind)"
            content="No withdrawal access"
            placement="bottom"
          >
            <div
              @click="proceedToWithdraw"
              :class="
                !withdrawalAccess.includes(user.kind)
                  ? 'cursor-not-allowed opacity-30'
                  : 'cursor-pointer'
              "
              class="bg-root-1 font-normal justify-around hover:opacity-80 flex text-primary items-center border border-primary rounded-3xl py-2 px-6 ml-2"
            >
              <div class="-ml-2 mr-2">-</div>
              <div>Withdraw</div>
            </div>
          </el-tooltip>
        </div>
        <div
          class="border border-gray-20 flex justify-center items-center h-12 w-12 mx-4 rounded-full"
        >
          <div class="relative cursor-pointer">
            <notification-icon class="h-6" />
            <div
              v-if="notifications.length > 0"
              class="flex items-center absolute -top-1.5 -right-1 text-white bg-red-1 rounded-full h-4 w-4 font-semibold text-7px justify-center"
            >
              1
            </div>
          </div>
        </div>
        <el-avatar class="bg-blue-10" :size="50"
          >{{ user.firstName.charAt(0).toUpperCase()
          }}{{ user.lastName.charAt(0).toUpperCase() }}</el-avatar
        >
      </div>
    </div>
    <!-- for small screens -->
    <div class="lg:hidden">
      <div
        class="small-screen-header background flex items-center justify-between px-6 md:px-14 py-4 mb-3"
      >
        <!-- left-sde -->
        <div class="flex items-center gap-3">
          <div @click="$emit('toggle-drawer')">
            <hamburger-icon />
          </div>

          <app-logo />
        </div>
        <!-- right-side -->
        <div class="flex">
          <div
            class="border border-gray-20 flex justify-center items-center h-7 w-7 md:h-9 md:w-9 mx-2 md:mx-3 rounded-full"
          >
            <div class="relative cursor-pointer">
              <notification-icon class="h-3 md:h-4" />
              <div
                v-if="notifications.length > 0"
                class="flex items-center absolute -top-1.5 -right-1 text-white bg-red-1 rounded-full h-2 w-2 font-semibold text-7px justify-center"
              >
                1
              </div>
            </div>
          </div>
          <el-avatar class="bg-blue-10" :size="avatarSize">{{
            user.firstName.charAt(0).toUpperCase()
          }}</el-avatar>
        </div>
      </div>
      <div
        v-if="routeName === 'dashboard'"
        class="px-6 md:px-14 mb-8 text-root-2"
      >
        <h3 class="text-lg md:text-xl">
          {{ getTimeofDay }},
          <span class="capitalize">{{ user.firstName }}</span>
        </h3>
        <div class="font-normal text-gray-10 text-xs md:text-sm">
          Overview Summary
        </div>
      </div>
    </div>
    <agreement-dialog
      v-if="showAgreementDialog"
      :showDialog="showAgreementDialog"
      :agreementData="agreementContract"
      @closeDialog="closeDialog"
      @agreementConfirmed="showDepositType"
    />
    <deposit-type-dialog
      v-if="showDepositTypeDialog"
      :showDialog="showDepositTypeDialog"
      @closeDialog="closeDepositTypeDialog"
      @hasPendingDeposit="showEditPendingDepositDialog = true"
      @showFiatCurrency="showDepositScreen('depositAmount')"
      @completedDeposit="showPendingDepositDialog = true"
      @showWallet="showWallet"
      :view="view"
      :transaction="currentTransaction"
    />
    <wallet-dialog
      v-if="showWalletDialog"
      @closeDialog="closeWalletDialog"
      @goBack="showDepositType"
      :showDialog="showWalletDialog"
      :walletAddress="walletAddress"
    />
    <deposit-amount-dialog
      v-if="showDepositAmountDialog"
      :showDialog="showDepositAmountDialog"
      @closeDialog="closeDepositAmountDialog"
      @completedDeposit="showPendingDepositDialog = true"
      :toShowTransferType="transferType"
    />
    <pending-deposit-dialog
      v-if="showPendingDepositDialog"
      :showDialog="showPendingDepositDialog"
      @closeDialog="closePendingDepositDialog"
      @completedDeposit="depositCompleted = true"
    />
    <edit-pending-deposit
      v-if="showEditPendingDepositDialog"
      :showDialog="showEditPendingDepositDialog"
      @closeDialog="closeEditPendingDepositDialog"
      @editPendingDeposit="showDepositAmountScreen"
      @showDepositDetails="showDepositScreen"
    />
    <deposit-pending-dialog
      v-if="showDepositPendingDialog"
      :showDialog="showDepositPendingDialog"
      @closeDialog="closePendingDepositDialog"
      @proceedToDeposit="proceedToDeposit"
      @showDepositDetails="showDepositDetails"
      :pendingDepositDetails="pendingDepositDetails"
    />
  </div>
</template>

<script>
import breakpoints from '@/utils/breakpoints.js'
import eventBus from '@/global/eventBus'
import { WITHDRAWAL_ACCESS, DEPOSIT_ACCESS } from '@/utils/constants'
import pendingDeposit from '@/mixins/pendingDeposit.js'
export default {
  mixins: [pendingDeposit],
  data: () => ({
    breakpoints,
    showDepositPendingDialog: false,
    notifications: [],
    depositAccess: DEPOSIT_ACCESS,
    withdrawalAccess: WITHDRAWAL_ACCESS,
    isLoading: false,
    showAgreementDialog: false,
    detailsInterval: null,
    showDepositAmountDialog: false,
    showPendingDepositDialog: false,
    showEditPendingDepositDialog: false,
    showDepositTypeDialog: false,
    showWalletDialog: false,
    depositCompleted: false,
    transferType: 'depositAmount',
    view: 'depositType'
  }),
  components: {
    AppLogo: () => import('@/assets/images/AppLogo.vue'),
    HamburgerIcon: () => import('@/assets/icons/HamburgerIcon.vue'),
    DepositPendingDialog: () =>
      import('@/components/deposit/DepositPendingDialog.vue'),
    NotificationIcon: () => import('@/assets/icons/NotificationIcon.vue'),
    AgreementDialog: () => import('@/components/deposit/AgreementDialog.vue'),
    DepositAmountDialog: () =>
      import('@/components/deposit/DepositAmountDialog.vue'),
    DepositTypeDialog: () =>
      import('@/components/deposit/DepositTypeDialog.vue'),
    WalletDialog: () => import('@/components/deposit/WalletDialog.vue'),
    PendingDepositDialog: () =>
      import('@/components/deposit/PendingDepositDialog.vue'),
    EditPendingDeposit: () =>
      import('@/components/deposit/EditPendingDeposit.vue')
  },
  methods: {
    proceedToWithdraw () {
      if (!this.withdrawalAccess.includes(this.user.kind)) {
        this.$message({
          showClose: true,
          message: 'No withdrawal access',
          type: 'info'
        })
      }
      this.$router.push('withdrawal')
    },
    goToDeposit () {
      if (this.pendingDepositDetails && this.hasPendingDeposit) {
        this.showDepositPendingDialog = true
      } else {
        this.proceedToDeposit()
      }
    },
    closePendingDepositDialog () {
      this.showDepositPendingDialog = false
    },
    showDepositDetails () {
      this.depositDetails()
    },
    proceedToDeposit () {
      if (
        !this.isbusinessAccountActive ||
        !this.depositAccess.includes(this.user.kind)
      ) {
        this.$message({
          showClose: true,
          message: !this.isbusinessAccountActive
            ? 'Business account is currently not active'
            : 'No deposit access',
          type: 'info'
        })
        return
      }
      if (
        (this.latestTransactions && this.latestTransactions.length > 0) ||
        this.isManualKycType
      ) {
        this.showDepositTypeDialog = true
      } else {
        this.isLoading = true
        const vm = this
        this.$store
          .dispatch('transactions/depositAgreement')
          .then(() => {
            this.showAgreementDialog = true
          })
          .catch((error) => {
            const err = error.response
              ? error.response.data.error.publicMessage
              : error
            vm.$message({
              showClose: true,
              message: this.httpErrorMessage,
              type: 'error'
            })
          })
          .finally(() => {
            this.isLoading = false
          })
      }
    },
    closeDialog () {
      this.showAgreementDialog = false
    },
    showDepositType () {
      this.showDepositTypeDialog = true
    },
    depositDetails () {
      if (
        this.transactionDetails.depositMethod ===
        this.$transactionType.LOCAL_TRANSFER
      ) {
        this.$store.dispatch(
          'transactions/setDepositTypeView',
          'localAccountInformation'
        )
      } else if (
        this.transactionDetails.depositMethod ===
        this.$transactionType.ASSET_TRANSFER
      ) {
        this.$store.dispatch(
          'transactions/setDepositTypeView',
          'assetAccountInformation'
        )
      } else {
        this.$store.dispatch(
          'transactions/setDepositTypeView',
          'accountInformation'
        )
      }
      this.showDepositTypeDialog = true
    },
    closeDepositAmountDialog () {
      this.showDepositAmountDialog = false
    },
    closeWalletDialog () {
      this.showWalletDialog = false
    },
    closeEditPendingDepositDialog () {
      this.showEditPendingDepositDialog = false
    },
    closeDepositTypeDialog () {
      this.showDepositTypeDialog = false
    },
    goToDashboard () {
      this.$router.push('dashboard')
    },
    toggleDrawer () {
      this.$emit('toggleDrawer')
    },
    showWallet (walletAddress) {
      this.walletAddress = walletAddress
      this.showWalletDialog = true
    },
    showDepositScreen (transferType) {
      this.showDepositAmountDialog = true
      this.transferType = transferType
    },
    showDepositAmountScreen (transferType) {
      this.showDepositAmountDialog = true
      this.transferType = transferType
    }
  },
  computed: {
    depositWithdrawAccess () {
      return process.env.VUE_APP_DEPOSIT_WITHDRAW_ACCESS === 'true'
    },
    latestTransactions () {
      if (
        !this.$store.state.transactions.transactions ||
        !this.$store.state.transactions.transactions.items
      ) {
        return []
      }
      return this.$store.state.transactions.transactions.items.slice(0, 3)
    },
    user () {
      return this.$store.state.auth.user
    },
    isManualKycType () {
      return this.$store.state.auth.business.kycType === 'manual'
    },
    routeName () {
      return this.$route.name
    },
    pageTitle () {
      return this.$route.meta.pageTitle
    },
    isbusinessAccountActive () {
      return this.$store.state.auth.business.accountActive
    },
    agreementContract () {
      return this.$store.state.transactions.depositContract
    },
    currentTransaction () {
      return this.$store.state.transactions.currentTransaction
    },
    transactionDetails () {
      const details = this.$store.state.transactions.transactionDetails
      return details
    },
    getTimeofDay () {
      var newDate = new Date()
      var hours = newDate.getHours()
      var message =
        hours < 12
          ? 'Good Morning'
          : hours < 18
          ? 'Good Afternoon'
          : 'Good Evening'
      return message
    },
    avatarSize () {
      return breakpoints.mdAndUp ? 35 : 27
    }
  },
  mounted () {
    eventBus.$on('depositAction', () => {
      this.proceedToDeposit()
    })
    eventBus.$on('depositDetailsEvent', () => {
      this.depositDetails()
    })
    eventBus.$on('showDepositTypeEvent', (transaction) => {
      this.currentTransaction = transaction
      this.showDepositTypeDialog = true
    })
  }
}
</script>
<style scoped>
.text-root-2 {
  color: var(--text-2);
}
.background-1 {
  background: var(--background-1);
}
.background {
  background: var(--background);
}
.small-screen-header {
  box-shadow:
    0px 0px 1px rgba(3, 2, 7, 0.12),
    0px 1px 3px rgba(3, 2, 7, 0.12);
}
</style>
